import { create } from 'zustand';

// API base URL
const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:3001/api';

// Comment types
export interface CommentUser {
  id: string;
  name: string;
  avatarUrl?: string;
}

export interface Comment {
  id: string;
  chapterId: string;
  userId: string;
  parentId?: string;
  type: 'comment' | 'suggestion';
  status: 'open' | 'resolved';
  selectionStart?: number;
  selectionEnd?: number;
  selectedText?: string;
  content: string;
  suggestedText?: string;
  resolvedBy?: string;
  resolvedAt?: string;
  createdAt: string;
  updatedAt: string;
  user: CommentUser;
  replies?: Comment[];
  resolver?: CommentUser;
}

export interface CreateCommentData {
  chapterId: string;
  type?: 'comment' | 'suggestion';
  selectionStart?: number;
  selectionEnd?: number;
  selectedText?: string;
  content: string;
  suggestedText?: string;
  parentId?: string;
}

interface CommentsState {
  // State
  comments: Comment[];
  isLoading: boolean;
  error: string | null;
  activeCommentId: string | null;

  // Actions
  fetchComments: (chapterId: string) => Promise<void>;
  addComment: (data: CreateCommentData) => Promise<Comment | null>;
  updateComment: (id: string, content: string) => Promise<void>;
  deleteComment: (id: string) => Promise<void>;
  resolveComment: (id: string) => Promise<void>;
  unresolveComment: (id: string) => Promise<void>;
  acceptSuggestion: (id: string) => Promise<{
    selectionStart?: number;
    selectionEnd?: number;
    selectedText?: string;
    suggestedText?: string;
  } | null>;
  setActiveComment: (id: string | null) => void;
  clearComments: () => void;
}

export const useCommentsStore = create<CommentsState>((set, get) => ({
  // Initial state
  comments: [],
  isLoading: false,
  error: null,
  activeCommentId: null,

  // Fetch comments for a chapter
  fetchComments: async (chapterId: string) => {
    set({ isLoading: true, error: null });

    try {
      const response = await fetch(`${API_BASE}/comments/chapter/${chapterId}`, {
        credentials: 'include',
      });

      // 404 is not an error - it just means the chapter exists but has no comments yet
      // or the chapter was just created
      if (response.status === 404) {
        set({ comments: [], isLoading: false });
        return;
      }

      if (!response.ok) {
        throw new Error('Failed to fetch comments');
      }

      const data = await response.json();
      set({ comments: data.comments, isLoading: false });
    } catch (error) {
      // Silently handle network errors for comments - they're not critical
      console.warn('Failed to fetch comments:', error);
      set({ comments: [], isLoading: false });
    }
  },

  // Add a new comment
  addComment: async (data: CreateCommentData) => {
    set({ isLoading: true, error: null });

    try {
      const response = await fetch(`${API_BASE}/comments`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        throw new Error('Failed to create comment');
      }

      const result = await response.json();
      const newComment = result.comment as Comment;

      set((state) => {
        // If it's a reply, add to parent's replies
        if (data.parentId) {
          return {
            comments: state.comments.map((c) =>
              c.id === data.parentId
                ? { ...c, replies: [...(c.replies || []), newComment] }
                : c
            ),
            isLoading: false,
          };
        }

        // Otherwise add as top-level comment
        return {
          comments: [newComment, ...state.comments],
          isLoading: false,
        };
      });

      return newComment;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to create comment';
      set({ error: message, isLoading: false });
      return null;
    }
  },

  // Update comment content
  updateComment: async (id: string, content: string) => {
    try {
      const response = await fetch(`${API_BASE}/comments/${id}`, {
        method: 'PATCH',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ content }),
      });

      if (!response.ok) {
        throw new Error('Failed to update comment');
      }

      // Update local state
      set((state) => ({
        comments: state.comments.map((c) => {
          if (c.id === id) {
            return { ...c, content, updatedAt: new Date().toISOString() };
          }
          // Check replies
          if (c.replies) {
            return {
              ...c,
              replies: c.replies.map((r) =>
                r.id === id ? { ...r, content, updatedAt: new Date().toISOString() } : r
              ),
            };
          }
          return c;
        }),
      }));
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to update comment';
      set({ error: message });
    }
  },

  // Delete comment
  deleteComment: async (id: string) => {
    try {
      const response = await fetch(`${API_BASE}/comments/${id}`, {
        method: 'DELETE',
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('Failed to delete comment');
      }

      // Remove from local state
      set((state) => ({
        comments: state.comments
          .filter((c) => c.id !== id)
          .map((c) => ({
            ...c,
            replies: c.replies?.filter((r) => r.id !== id),
          })),
        activeCommentId: state.activeCommentId === id ? null : state.activeCommentId,
      }));
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to delete comment';
      set({ error: message });
    }
  },

  // Resolve comment
  resolveComment: async (id: string) => {
    try {
      const response = await fetch(`${API_BASE}/comments/${id}/resolve`, {
        method: 'PATCH',
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('Failed to resolve comment');
      }

      // Update local state
      set((state) => ({
        comments: state.comments.map((c) =>
          c.id === id
            ? { ...c, status: 'resolved' as const, resolvedAt: new Date().toISOString() }
            : c
        ),
      }));
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to resolve comment';
      set({ error: message });
    }
  },

  // Unresolve comment
  unresolveComment: async (id: string) => {
    try {
      const response = await fetch(`${API_BASE}/comments/${id}/unresolve`, {
        method: 'PATCH',
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('Failed to unresolve comment');
      }

      // Update local state
      set((state) => ({
        comments: state.comments.map((c) =>
          c.id === id
            ? { ...c, status: 'open' as const, resolvedAt: undefined, resolvedBy: undefined }
            : c
        ),
      }));
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to unresolve comment';
      set({ error: message });
    }
  },

  // Accept suggestion - returns the suggestion data to apply in editor
  acceptSuggestion: async (id: string) => {
    try {
      const response = await fetch(`${API_BASE}/comments/${id}/accept`, {
        method: 'POST',
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('Failed to accept suggestion');
      }

      const data = await response.json();

      // Resolve the comment after accepting
      await get().resolveComment(id);

      return data.suggestion;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to accept suggestion';
      set({ error: message });
      return null;
    }
  },

  // Set active comment (for highlighting in editor)
  setActiveComment: (id: string | null) => {
    set({ activeCommentId: id });
  },

  // Clear comments (when changing chapters)
  clearComments: () => {
    set({ comments: [], activeCommentId: null, error: null });
  },
}));

// Helper hooks
export function useCommentsByRange(from: number, to: number): Comment[] {
  const comments = useCommentsStore((s) => s.comments);
  return comments.filter(
    (c) =>
      c.selectionStart !== undefined &&
      c.selectionEnd !== undefined &&
      c.status === 'open' &&
      // Check if ranges overlap
      c.selectionStart <= to &&
      c.selectionEnd >= from
  );
}

export function useOpenComments(): Comment[] {
  const comments = useCommentsStore((s) => s.comments);
  return comments.filter((c) => c.status === 'open');
}

export function useResolvedComments(): Comment[] {
  const comments = useCommentsStore((s) => s.comments);
  return comments.filter((c) => c.status === 'resolved');
}
